import * as React from 'react';
import { compose, withProps, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { skin, OemProps } from 'ui/styles/skin';
import { State } from 'reducers';
import { DeviceIndex, UtcTime, Camera, DeviceId } from 'types';
import { isLoadingDevices, HasDeviceIndex, HasDeviceId, getCamera } from 'selectors/deviceSelectors';
import { getStreamPosition, HasCellIndex } from 'selectors/monitoringSelectors';
import { ErrorMessage, waitUntilLoaded } from 'ui/shared';
import { ChangeImageCell } from './ChangeImageCell';

import './ChangeImagePage.css';

type RouterProps = RouteComponentProps<{}>;

//
//  This is used when the change image functionality is called from an external source
//  (e.g. ServerConfig analytics configuration) and hosted in an <iframe/>
//

const centeredStyle = {
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
    left: '50%',
    top: '50%',
};

export const RawChangeImagePage = ({ camera, styles }: StateProps & OemProps) =>
    <div id="change-image-page" className="abs page" style={ styles.Workspace }>
        { camera
            ? <ChangeImageCell
                busy={ false }
                visible={ true }
                deviceIndex={ camera.deviceIndex }
            />
            : <ErrorMessage
                style={ centeredStyle }
                textKey="6.1237.3262"
            />
        }
    </div>
;

type StateProps = {
    loading: boolean;
    streamPosition: UtcTime;
    camera: Camera | null;
};
type RecomposeProps = {
    deviceIndex: DeviceIndex | null;
    eventName: string | null;
    handleAccept: (base64Image: string, utcTime: UtcTime) => void;
    handleCancel: () => void;
};

const getDeviceIdFromQueryParams = (query: string): DeviceId | null => {
    const match = /(?:^|\?|&)c=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:&|$)/i.exec(query);
    return match && match[1].toLowerCase();
};

const getDeviceIndexFromQueryParams = (query: string): DeviceIndex | null => {
    const match = /(?:^|\?|&)c=(\d+)(?:&|$)/.exec(query);
    return match && parseInt(match[1], 10);
};

const getEventNameFromQueryParams = (query: string): string | null => {
    const match = /(?:^|\?|&)e=(.*?)(?:&|$)/.exec(query);
    return match && match[1];
};

const changeImageCellIndex: HasCellIndex = { cellIndex: 0 };

const mapStateToProps = (state: State, ownProps: HasDeviceId | HasDeviceIndex) => ({
    loading: isLoadingDevices(state),
    streamPosition: getStreamPosition(state, changeImageCellIndex),
    camera: getCamera(state, ownProps),
});

export const ChangeImagePage = compose<StateProps & OemProps, {}>(
    skin,
    withRouter,
    withProps(({ location }: RouterProps) => ({
        deviceId: getDeviceIdFromQueryParams(location.search),
        deviceIndex: getDeviceIndexFromQueryParams(location.search),
        eventName: getEventNameFromQueryParams(location.search),
    })),
    connect<StateProps, {}, {}>(mapStateToProps),
    lifecycle<RecomposeProps & StateProps, {}>({
        componentDidUpdate(oldProps) {
            const { streamPosition, eventName } = this.props;
            if (oldProps.streamPosition !== streamPosition) {
                // Post the frame time back to the parent window.
                const message = `${eventName}:${streamPosition}`;
                parent.postMessage(message, `https://${location.hostname}${location.port}`);
            }
        },
    }),
    waitUntilLoaded({ size: 'xlarge' }),
)(RawChangeImagePage);



// WEBPACK FOOTER //
// ./src/ui/pages/changeImage/ChangeImagePage.tsx